import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/shared/ProtectedRoute';
import Layout from './components/shared/Layout';
import Dashboard from './pages/Dashboard';
import Cases from './pages/Cases';
import CaseDetail from './pages/CaseDetail';
import Billing from './pages/Billing';
import Mediators from './pages/Mediators';
import MediatorDetail from './pages/MediatorDetail';
import Inquiries from './pages/Inquiries';
import InquiryDetail from './pages/InquiryDetail';
import Schedule from './pages/Schedule';
import BookingLink from './pages/BookingLink';
import EmailTemplates from './pages/EmailTemplates';
import Signatures from './pages/Signatures';
import SignatureDetail from './pages/SignatureDetail';
import TemplateManager from './pages/TemplateManager';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CreateInvoice from './pages/CreateInvoice';
import UpdateInvoiceRouter from './pages/UpdateInvoiceRouter';

/**
 * AppRoutes — Scale-parity modal-route pattern.
 *
 * /hourly-billing/:caseId and /update-invoice/:caseId render as MODAL
 * routes layered over the case detail page. When navigated to with
 * `state: { backgroundLocation }`, the previous page (Case Detail) keeps
 * rendering as the backdrop and we render the modal route on top.
 *
 * If you load /hourly-billing/:caseId directly (no backgroundLocation),
 * we synthesize one pointing at /cases/:caseId so the user still gets
 * the same blurred-case-detail-behind-modal experience.
 */
function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;

  // Synthesize a backdrop for direct loads of /hourly-billing/:caseId or
  // /update-invoice/:caseId so the visual matches Scale even when there
  // is no client-side navigation history.
  const isModalRoute = /^\/(hourly-billing|update-invoice)\//.test(location.pathname);
  const inferredBackground = (() => {
    if (!isModalRoute || backgroundLocation) return null;
    const m = location.pathname.match(/^\/(?:hourly-billing|update-invoice)\/([^/]+)/);
    if (!m) return null;
    return { pathname: `/cases/${m[1]}` };
  })();
  const baseLocation = backgroundLocation || inferredBackground || location;

  return (
    <>
      <Routes location={baseLocation}>
        {/* Standalone pages (no Layout/Sidebar) */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* App pages with Layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/cases" element={<Cases />} />
                  <Route path="/cases/:id" element={<CaseDetail />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/mediators" element={<Mediators />} />
                  <Route path="/mediators/:id" element={<MediatorDetail />} />
                  <Route path="/inquiries" element={<Inquiries />} />
                  <Route path="/inquiries/:id" element={<InquiryDetail />} />
                  <Route path="/schedule" element={<Schedule />} />
                  <Route path="/booking-link" element={<BookingLink />} />
                  <Route path="/signatures" element={<Signatures />} />
                  <Route path="/signatures/:id" element={<SignatureDetail />} />
                  <Route path="/signatures/templates" element={<TemplateManager />} />
                  <Route path="/email-templates" element={<EmailTemplates />} />
                  <Route path="/settings" element={<Settings />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Modal routes — render OVER the base location when active. The
          modal pages own their own backdrop/blur so we don't duplicate it
          here. */}
      {isModalRoute && (
        <Routes>
          <Route path="/hourly-billing/:caseId" element={<ProtectedRoute><CreateInvoice /></ProtectedRoute>} />
          {/* UpdateInvoiceRouter picks the fixed or the hourly editor and
              renders pages/UpdateInvoice (fixed) unchanged as its default. */}
          <Route path="/update-invoice/:caseId" element={<ProtectedRoute><UpdateInvoiceRouter /></ProtectedRoute>} />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
